import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild
} from "@angular/core";
import { TooltipPosition } from "@angular/material/tooltip";
import { MatDialog } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import {
  UploadFileResultError,
  UploadFileResponse,
  UploadFileResultModalProps,
  UploadFileStatusResponse,
  UploadOption
} from "../download-upload.types";
import { DownloadUploadService } from "../download-upload.service";
import { FileUploadResultModalComponent } from "../components/file-upload-result-modal/file-upload-result-modal.component";
import { NotifyService } from "@core/services/notify.service";

@Component({
  selector: "app-driveb-upload-file",
  templateUrl: "./upload-file.component.html",
  styleUrls: ["./upload-file.component.scss"],
  standalone: false
})
export class UploadFileComponent implements OnInit {
  @Input() options: UploadOption[] = [];
  @Input() positionTooltip: TooltipPosition = "right";
  @Input() tooltip = "";
  @Input() acceptFiles = ".xlsx, .xls, .csv";
  @Output() afterImport = new EventEmitter();
  @ViewChild("fileInput", { static: false })
  fileInput!: ElementRef<HTMLInputElement>;
  @Input() uploadButtonText = "";

  fontIcon = "Bosch-Ic-export";
  file: File | null = null;
  selectedOption: UploadOption | null = null;

  constructor(
    private translateService: TranslateService,
    private downloadUploadService: DownloadUploadService,
    private dialog: MatDialog,
    private notifyService: NotifyService
  ) {}

  ngOnInit() {
    this.setDefaultTooltip();
  }

  setDefaultTooltip() {
    if (!this.tooltip) {
      const tooltipDefault = `download-upload-file.upload`;
      this.tooltip = this.translateService.instant(tooltipDefault);
    }
  }

  handleFile(file: File) {
    this.file = file;
  }

  removeFile() {
    this.selectedOption = null;
    this.file = null;
    this.fileInput.nativeElement.value = "";
  }

  openUploadDialog(option: UploadOption) {
    this.selectedOption = option;
    this.fileInput.nativeElement.click();
  }

  save() {
    if (!this.file || !this.selectedOption) {
      return;
    }

    this.downloadUploadService
      .uploadFile(this.file, this.selectedOption)
      .subscribe(
        (data) => {
          this.removeFile();
          this.openResultModal(this.normalizeUploadResponse(data));
        },
        (err) => {
          this.notifyService.showErrorMessage(
            err.error?.message
              ? err.error.message
              : this.translateService.instant("default-error-message")
          );
          this.removeFile();
        }
      );
  }

  openResultModal(response: string[] | UploadFileResponse[]) {
    const title = this.translateService.instant(
      "file-upload-result-modal.result-modal"
    );

    const data: UploadFileResultModalProps = { title, response };

    const dialogRef = this.dialog.open(FileUploadResultModalComponent, {
      data
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.afterImport.next("");
      }
    });
  }

  private normalizeUploadResponse(
    response: string[] | UploadFileResponse[] | UploadFileStatusResponse
  ): string[] | UploadFileResponse[] {
    if (Array.isArray(response)) {
      return response;
    }

    if (!this.hasUploadErrors(response)) {
      return [];
    }

    if (response.errors?.length) {
      return response.errors.map((error) => this.mapUploadError(error));
    }

    return [this.sanitizeMessage(response.message)];
  }

  private hasUploadErrors(response: UploadFileStatusResponse): boolean {
    const status = response?.status?.toUpperCase();

    return (
      status === "ERROR" ||
      status === "BAD_REQUEST" ||
      !!response?.errors?.length
    );
  }

  private mapUploadError(error: UploadFileResultError): UploadFileResponse {
    return {
      linePosition: error.line || 0,
      rowValues: [error.type, error.field, error.value]
        .filter(
          (value) => value !== undefined && value !== null && value !== ""
        )
        .join(" - "),
      errorMessage: this.sanitizeMessage(error.message)
    };
  }

  private sanitizeMessage(message?: string): string {
    return (
      message?.replace(/^"|"$/g, "") ||
      this.translateService.instant("default-error-message")
    );
  }
}
